<template>
  <div class="p-4 bg-trader-gray flex flex-col overflow-hidden">
    <div class="space-y-4 flex flex-col overflow-auto max-h-[1000px]">
      <!-- Für jeden definierten Fehlertyp -->
      <template v-for="errorType in errorTypes" :key="errorType.code">
        <!-- Zeige die Tabelle nur, wenn Fehler dieses Typs vorhanden sind -->
        <div v-if="filteredErrors(errorType.code).length" class="bg-trader-light-gray rounded-md shadow-md p-4">
          <div class="flex items-center space-x-2 mb-4">
            <!-- Icon und Titel -->
            <svg
                class="w-6 h-6"
                :class="errorType.code === 'PRODUCT_NUMBER_NOT_FOUND' ? 'text-trader-dark-red' : 'text-trader-dark-yellow'"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
            >
              <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  :d="'M13 16h-1v-4h-1m1-4h.01M12 20h.01M19 12a7 7 0 11-14 0 7 7 0 0114 0z'"
              />
            </svg>
            <h1 class="text-lg font-bold text-trader-dark-yellow">
              {{ errorType.title }}
            </h1>
          </div>
          <!-- Tabellarische Darstellung -->
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-trader-yellow">
            <tr>
              <!-- Überschriften werden nur einmal ausgegeben -->
              <th
                  v-for="detail in errorType.details"
                  :key="detail.key"
                  class="px-6 py-3 text-left text-xs font-medium text-trader-dark-yellow uppercase tracking-wider"
              >
                {{ detail.label }}
              </th>
            </tr>
            </thead>
            <tbody class="bg-trader-light-gray divide-y divide-trader-gray">
            <tr v-for="(error, idx) in filteredErrors(errorType.code)" :key="errorType.code + '-' + idx">
              <!-- Pro Fehler wird eine Zeile erzeugt mit den jeweiligen Werten in den Spalten -->
              <td
                  v-for="detail in errorType.details"
                  :key="detail.key"
                  class="px-6 whitespace-nowrap text-trader-dark-yellow"
              >
                <!-- Falls für diesen Spalteneintrag ein anderes Element (Select oder Button) definiert ist -->
                <template v-if="detail.type === 'select'">
                  <!-- Select-Feld nur anzeigen, wenn der "createArticle"-Button noch nicht erfolgreich war -->
                  <div v-if="!buttonStates[`${errorType.code}-${idx}-createArticle`]?.success">
                    <select
                        v-model="error[detail.key]"
                        @change="handleSelectChange($event, error, detail, idx, errorType.code)"
                        class="form-select"
                    >
                      <!-- Dynamische Optionen für 'purchaser' -->
                      <template v-if="detail.key === 'purchaser'">
                        <option
                            v-for="(purchaser, purchaserIdx) in purchasers"
                            :key="purchaser.id"
                            :value="purchaser.id"
                        >
                          {{ purchaser.teamIcon }} - {{ purchaser.name }}
                        </option>
                      </template>
                      <template v-else>
                        <option
                            v-for="option in detail.options"
                            :key="option.value"
                            :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </template>
                    </select>
                  </div>
                  <span v-else>
                  {{ getSelectedLabel(error[detail.key]) }}
                </span>
                </template>
                <template v-else-if="detail.type === 'button'">
                  <div class="flex items-center space-x-2">
                    <div v-if="!buttonStates[errorType.code + '-' + idx + '-' + detail.actionType]?.success">
                      <button :data-ui="detail.label + idx"
                          @click="handleAction(error, detail.actionType, errorType.code + '-' + idx + '-' + detail.actionType)"
                          class="px-4 py-2 text-black  rounded-md shadow-md  bg-trader-dark-blue hover:bg-trader-blue">
                      <span v-if="(!buttonStates[errorType.code + '-' + idx + '-' + detail.actionType]?.loading)">
                        {{ detail.buttonText || 'Aktion ausführen' }}
                      </span>
                      </button>
                    </div>

                    <span v-if="buttonStates[errorType.code + '-' + idx + '-' + detail.actionType]?.success">
                      <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-green-500" fill="none"
                           viewBox="0 0 24 24"
                           stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                      </svg>
                    </span>
                  </div>
                </template>
                <template v-else>
                  {{ detail.transform ? detail.transform(getValue(error, detail.key)) : getValue(error, detail.key) }}
                </template>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
    <div class="flex justify-center mt-4">
      <button data-ui="reload-page"
          @click="reloadPage"
          class="px-4 py-2 text-black bg-trader-dark-blue rounded-md shadow-md hover:bg-trader-blue"
      >
        OK
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import {mutation, query} from "@/graphql";
import SignalBus from "@/application/SignalBus";
import {defineProps, onMounted, reactive, ref} from 'vue';

const props = defineProps({
  errors: {
    type: Array,
    default: () => []
  },
  messages: Array,
});


const buttonStates = reactive<Record<string, { loading: boolean; success: boolean }>>({});
const selectStates = reactive<Record<string, { loading: boolean; success: boolean }>>({});

const purchasers = ref([]);

function getValue(error: any, key: string): string {
  const found = error.data?.find((item: any) => item.key === key);
  return found ? found.value : '';
}


function filteredErrors(code: string) {
  let errors = props.errors.filter((error: any) => error.code === code);

  //));

  if (code === "PRODUCT_NUMBER_NOT_FOUND") {
    errors = errors.map((error: any) => {
      error.purchaser = error.purchaser || purchasers.value?.[0]?.id;
      return error;
    });
  }

  return errors;
}

const errorTypes = [
  {
    code: 'PRODUCT_NUMBER_NOT_FOUND',
    title: 'Folgende Artikel existieren nicht',
    details: [
      {key: 'PRODUCT_NUMBER', label: 'Artikelnummer'},
      {
        key: 'purchaser',
        label: 'Einkaufsbereich',
        type: 'select',
        actionType: 'selectPurchaser'
        // Es werden hier keine Options definiert, da diese dynamisch
        // aus "shoppingAreas" generiert werden.
      },
      {
        key: 'ACTION',
        label: 'Aktion ausführen',
        type: 'button',
        buttonText: 'Ausführen',
        actionType: 'createArticle'
      }
    ]
  },
  {
    code: 'PRODUCT_IS_ON_MAGAZINE',
    title: 'Folgende Artikel befindet sich schon im Magazin',
    details: [
      {key: 'PRODUCT_NUMBER', label: 'Artikelnummer'},
      {key: 'PAGE_POSITION', label: 'Seite'},
      {key: 'MARKET_NUMBER', label: 'Markt'}
    ]
  },
  {
    code: 'PRODUCT_HAS_WRONG_PURCHASER',
    title: 'Folgende Artikel hatten einen anderen Einkaufsbereich',
    details: [
      {key: 'PRODUCT_NUMBER', label: 'Artikelnummer'},
      {key: 'PURCHASER_ID', label: 'Einkaufsbereich'},
    ]
  },
  {
    code: 'PRODUCT_NO_MATCHING_PLACEHOLDER',
    title: 'Folgende Artikel hatten keinen passenden Platzhalter',
    details: [
      {key: 'PRODUCT_NUMBER', label: 'Artikelnummer'},
      {
        key: 'MARKET_NUMBERS',
        label: 'Märkte',

      }
    ]
  },
  {
    code: 'PRODUCT_IS_IN_CLIPBOARD',
    title: 'Folgende Artikel befindet sich schon in der Zwischenablage',
    details: [
      {key: 'PRODUCT_NUMBER', label: 'Artikelnummer'},
      {
        key: 'ACTION',
        label: 'Aus der Zwischenablage platzieren',
        type: 'button',
        buttonText: 'Zwischenablage',
        actionType: 'clipboard'
      },
      {
        key: 'ACTION',
        label: 'Aus dem Stammartikel platzieren',
        type: 'button',
        buttonText: 'Stammartikel',
        actionType: 'baseArticle'
      }
    ]
  },
  {
    code: 'MORE_PLACEHOLDERS_THAN_PRODUCTS',
    title: 'Nicht genügend Artikel um alle Platzhalter zu füllen',
    details: [
      {key: 'PLACEHOLDER_NUMBERS', label: 'Platzhalter'}
    ]

  },
  {
    code: 'MORE_PRODUCTS_THAN_PLACEHOLDERS',
    title: 'Kein Platz mehr um diese Artikel zu platzieren',
    details: [
      {
        key: 'PRODUCT_NUMBERS',
        label: 'Artikelnummer',
        transform: (value: string) => JSON.parse(value).join(' ')
      }
    ]
  },
  {
    code: 'PRODUCT_NOT_ON_MAGAZINE',
    title: 'Folgende Artikel konnten nicht im Quellmagazin gefunden werden',
    details: [
      {key: 'PRODUCT_NUMBER', label: 'Artikelnummer'}
    ]
  },

];

function reloadPage() {
  location.reload();
}
function getSelectedLabel(key) {
  // find in purchasers the purchaser with error[detail.key] and give back the name and icon
 return  purchasers.value.find(purchaser => purchaser.id === key)?.teamIcon + " - " + purchasers.value.find(purchaser => purchaser.id === key)?.name;
}
async function handleAction(error: any, actionType: string, key: string) {
  // Setze den Lade-Status für diesen Button
  buttonStates[key] = {loading: true, success: false};
  selectStates[key] = { loading: true, success: false };
  try {
    if (actionType === "createArticle") {
      await placeNewProduktOnPage(error);
    } else if (actionType === "clipboard") {
      await placeProductOutOfClipboard(error, 1);
    } else if (actionType === "baseArticle") {
      await placeProductOutOfClipboard(error, 2);
    }
    // Bei Erfolg: grüner Haken anzeigen
    buttonStates[key].success = true;
    selectStates[key].success = true;
  } catch (e) {
    console.error(e);
  } finally {
    // Lade-Status beenden
    buttonStates[key].loading = false;
    selectStates[key].loading = false;
    SignalBus.emit("reload-page");
  }
}
async function placeNewProduktOnPage(error: any) {
  const value = {
    productNumber: getValue(error, 'PRODUCT_NUMBER'),
    virtualId: getValue(error, 'VIRTUAL_ID'),
    pageId: getValue(error, 'PAGE_ID'),
    purchaserId: error.purchaser
  };

  const [data, err] = await mutation.placeNewProduktOnPage(value.pageId, value.productNumber, value.purchaserId, value.virtualId);
  if (err) {
    console.error(err);
    return;
  }
}

async function placeProductOutOfClipboard(error: any, rule: 0, index) {
  const value = {
    productNumber: getValue(error, 'PRODUCT_NUMBER'),
    virtualId: getValue(error, 'VIRTUAL_ID'),
    pageId: getValue(error, 'PAGE_ID'),
    purchaserId: error.purchaser
  };

  try {
    const [data, err] = await query.placeProductByRule(value.pageId, value.productNumber, value.virtualId, rule);
    if (err) {
      console.error(err);
      return;
    }
  } catch (e) {
    console.error(e);
  } finally {

  }
}


async function getPurchaserForCurrentUser() {
  const [data, err] = await query.getPurchaserForCurrentUser();
  if (err) {
    console.error(err);
    return;
  }
  //
  purchasers.value = data
}


onMounted(() => {
  getPurchaserForCurrentUser();
});


function handleSelectChange(event: Event, error: any, detail: any) {
  const target = event.target as HTMLSelectElement;
  // Da die Options aus dem shoppingAreas-Array stammen, entspricht der
  // ausgewählte Wert der ID des entsprechenden Einkaufsbereichs.
  const selectedAreaId = target.value;
}
</script>
